import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { practiceService } from '../models/database';
import { sortPracticesByDistance } from '../utils/locationUtils';

const Hero = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    postcode: '',
    issue: ''
  });
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  
  // Fetch practices on component mount
  useEffect(() => {
    fetchPractices();
  }, []);
  
  // Fetch all practices
  const fetchPractices = async () => {
    try {
      setLoading(true);
      const practicesData = await practiceService.getAllPractices();
      setPractices(practicesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching practices:', err);
      setError('Failed to load practices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Function to handle form submission
  const handleCaptchaSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.postcode) {
      setError('Please enter your postcode to find the nearest practice.');
      return;
    }

    // Execute reCAPTCHA when form is submitted
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {action: 'submit'})
        .then(token => {
          // After getting token, proceed with form submission
          submitForm(token);
        })
        .catch(err => {
          console.error('reCAPTCHA error:', err);
          setError('Failed to verify you are not a robot. Please try again.');
        });
    });
  };

  // Function to submit the form after reCAPTCHA verification
  const submitForm = async (captchaToken) => {
    if (!captchaToken) {
      setError('Please verify that you are not a robot.');
      return;
    }
    
    try {
      setLoading(true);
      
      // Sort practices by distance from the user's postcode
      const sortedPractices = await sortPracticesByDistance(formData.postcode, practices);
      
      // Ensure sortedPractices is an array
      const practicesArray = Array.isArray(sortedPractices) ? sortedPractices : [];
      
      if (practicesArray.length === 0) {
        setError('No practices found. Please try a different postcode.');
        setLoading(false);
        return;
      }
      
      // Navigate to the AvailabilityChecker component with the form data and nearest practice
      navigate('/availability-check', { 
        state: { 
          formData,
          nearestPractice: practicesArray[0],
          sortedPractices: practicesArray.slice(0, 5) // Send the 5 nearest practices
        } 
      });
      
    } catch (err) {
      console.error('Error finding nearest practice:', err);
      setError('Failed to find nearest practice. Please try again.');
      setLoading(false);
    }
  };


  return (
    <div id="hero" className="bg-gradient-to-br from-primary to-secondary py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          {/* Hero Content */}
          <div className="lg:w-1/2">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-6">
              Dental Emergency? We can help!
            </h1>
            <p className="text-lg md:text-xl text-white mb-8">
              Get Same-Day Relief from Pain with Our Emergency Dentists – 
              <span className="font-bold block mt-2">
                Expert Care for Just £39 Booking Fee & a Fixed Treatment Cost!
              </span>
            </p>

            {/* Scroll to appointment checker button */}
            <button 
              onClick={() => {
                const contactSection = document.getElementById('contact-section');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-accent hover:bg-accent/90 text-white font-medium py-3 px-8 rounded-full text-lg inline-block transition-colors"
            >
              Check Today's Appointments
            </button>
          </div>
          
          {/* Availability Checker Form */}
          <div className="lg:w-1/2 bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-primary font-heading mb-4">
              Instant Availability Checker
            </h2>
            <p className="text-gray-700 mb-4">
              Check our Emergency Dentist Availability in your Area – Simply Enter your Details Below
            </p>
            
            <form onSubmit={handleCaptchaSubmit} className="space-y-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                className="w-full p-3 border border-gray-300 rounded"
                required
              />
              
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone Number"
                className="w-full p-3 border border-gray-300 rounded"
                required
              />
              
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                placeholder="Your Postcode"
                className="w-full p-3 border border-gray-300 rounded"
                required
              />
              
              {/* <textarea
                name="issue"
                value={formData.issue}
                onChange={handleChange}
                placeholder="What's your issue?"
                className="w-full p-3 border border-gray-300 rounded"
                rows="4"
                required
              ></textarea> */}
              
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-accent hover:bg-accent/90 text-white font-medium py-3 px-6 rounded transition-colors ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Finding nearest practice...
                  </span>
                ) : (
                  'Find an appointment'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
